import { useFormContext } from 'react-hook-form';
import { useState, useEffect } from 'react';
import Stack from '@mui/system/Stack/Stack';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import Divider from '@mui/material/Divider';
import { Box } from '@mui/material';
import ControlledRadioInput from '../../../../components/custom-inputs/ControlledRadioInput';
import BaseInputLabel from '../../../../components/custom-inputs/BaseInputLabel';
import CustomInput from '../../../../components/custom-input/CustomInput';
import CustomButton from '../../../../components/button/CustomButton';
import { initialBuyerState, initialSellerState } from '../utils';
import { secondaryBlue, secondaryGreen, primaryDark } from '../../../../utils/color';
import { TUserType } from '../interface';
import { CompanyRole, mapCountry, mapIndustry, mapLanguage } from 'tdc-web-backend/enums/enums';
import { useCreateSessionFromUrl } from '../hooks/useCreateSessionFromUrl';

interface Details {
  company_industry?: string;
  company_size?: string;
  offered_services?: string;
  operating_currency?: string;
  target_market?: string;
  working_language?: string;
}

interface Overview {
  city: string;
  company_description?: string;
  company_name?: string;
  headquarters_location?: string;
  logo_url: string;
  website_url: string;
}
interface SessionData {
  context?: {
    details?: Details;
    overview?: Overview;
  };
  created_at?: string;
  external_user_id?: string;
  id?: string;
  kind?: string;
  title: string;
  user_id?: string;
}

// Helper function to find enum key by value from a map
const findEnumKeyByValue = (map: Map<any, string>, value: string): any | null => {
  for (const [key, val] of Array.from(map.entries())) {
    if (val.toLowerCase() === value.toLowerCase()) {
      return key;
    }
  }
  return null;
};

const sxRadio = {
  padding: '24px',
  border: `1px solid`,
  borderRadius: '6px',
  marginLeft: '0px',
  '.MuiTypography-root': {
    fontSize: '14px',
    fontFamily: 'Gellix-SemiBold',
    color: primaryDark[500],
  },
};

const sxYesNoButton = {
  padding: '24px',
  border: `1px solid`,
  borderRadius: '6px',
  marginLeft: '0px',
  '.MuiTypography-root': {
    fontSize: '14px',
    fontFamily: 'Gellix-SemiBold',
    color: primaryDark[500],
  },
};

const sxBoldText = { fontFamily: 'Gellix-SemiBold', color: primaryDark[500] };

const HELPER_MESSAGES = {
  seller_service: (
    <>
      Be sure to choose the correct user type! If your company offers digital services and is
      looking for new clients, your user type is <b style={sxBoldText}>seller</b>.
    </>
  ),
  buyer: (
    <>
      Be sure to choose the correct user type! If your company is looking to hire service providers
      for digital projects, your user type is <b style={sxBoldText}>buyer</b>.
    </>
  ),
};

const User = ({
  setDataSubmitting,
  sessions,
  onSessionCreated,
}: {
  setDataSubmitting: (value: boolean) => void;
  sessions?: SessionData[];
  onSessionCreated?: (session: SessionData) => void;
}) => {
  const { watch, reset, setValue } = useFormContext();
  const userValue: TUserType = watch('user');
  const websiteUrl = watch('websiteUrl');
  const isBuyer = userValue === CompanyRole.Buyer;
  const isSeller = userValue === CompanyRole.SellerService;
  const [useAI, setUseAI] = useState<boolean | null>(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [sessionUrl, setSessionUrl] = useState<string>('');

  // Filter sessions by kind 'post_registration_assistant'
  const registrationSessions =
    sessions?.filter((session) => session.kind === 'post_registration_assistant') || [];
  const hasExistingSession = registrationSessions.length > 0;

  // custom hook to create session and fetch data from URL
  const { fetchData, isLoading } = useCreateSessionFromUrl();

  // Reusable function to populate fields from session data
  const populateFieldsFromSessionData = (data: SessionData, sourceUrl?: string) => {
    const overviewData = data?.context?.overview;
    const detailsData = data?.context?.details;

    // Reset details fields first to clear previous values
    setValue('details.targetIndustries', [], { shouldDirty: true, shouldValidate: true });
    setValue('details.targetCountries', [], { shouldDirty: true, shouldValidate: true });
    setValue('details.companySize', '', { shouldDirty: true, shouldValidate: true });
    setValue('details.languages', [], { shouldDirty: true, shouldValidate: true });
    setValue('details.operatingCurrency', '', { shouldDirty: true, shouldValidate: true });

    // Set website URL and AI website URL
    const urlToUse = sourceUrl || data.title || overviewData?.website_url || '';
    setValue('aiWebsiteUrl', urlToUse, { shouldDirty: true, shouldValidate: true });
    setValue('websiteUrl', urlToUse, { shouldDirty: true, shouldValidate: true });
    setValue('overview.name', overviewData?.company_name || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('overview.url', overviewData?.website_url || urlToUse, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Parse headquarters location (format: "City, Country")
    const locationParts =
      overviewData?.headquarters_location?.split(',').map((s: string) => s.trim()) || [];
    const cityValue = overviewData?.city || locationParts[0] || '';
    const countryName = locationParts[1] || '';
    const countryEnum = countryName ? findEnumKeyByValue(mapCountry, countryName) : null;

    setValue('overview.headquartersLocation.city', cityValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('overview.headquartersLocation.country', countryEnum || '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('overview.headquartersLocation.state', '', { shouldDirty: true });
    setValue('overview.description', overviewData?.company_description || '', {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Map details data
    const industries =
      detailsData?.company_industry
        ?.split(',')
        .map((ind: string) => ind.trim())
        .filter(Boolean) || [];

    if (industries.length > 0) {
      const industryEnums = industries
        .map((ind: string) => findEnumKeyByValue(mapIndustry, ind))
        .filter(Boolean);
      if (industryEnums.length > 0) {
        setValue('details.targetIndustries', industryEnums, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }

    const targetMarkets =
      detailsData?.target_market
        ?.split(',')
        .map((market: string) => market.trim())
        .filter(Boolean) || [];
    if (targetMarkets.length > 0) {
      const countryEnums = targetMarkets
        .map((country: string) => findEnumKeyByValue(mapCountry, country))
        .filter(Boolean);
      if (countryEnums.length > 0) {
        setValue('details.targetCountries', countryEnums, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }

    // Set company size
    if (detailsData?.company_size && detailsData?.company_size !== 'No information found') {
      setValue('details.companySize', detailsData?.company_size, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    // Parse working_language (comma-separated string like "English, German")
    const languages =
      detailsData?.working_language
        ?.split(',')
        .map((lang: string) => lang.trim())
        .filter(Boolean) || [];
    if (languages.length > 0) {
      const languageEnums = languages
        .map((lang: string) => findEnumKeyByValue(mapLanguage, lang))
        .filter(Boolean);
      if (languageEnums.length > 0) {
        setValue('details.languages', languageEnums, { shouldDirty: true, shouldValidate: true });
      }
    }

    // Set operating currency if available
    if (detailsData?.operating_currency) {
      setValue('details.operatingCurrency', detailsData?.operating_currency, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    setValue('aiAssistanceEnabled', true, { shouldDirty: true });

    // Store the session URL for comparison
    setSessionUrl(urlToUse);
  };

  // Auto-populate fields from the last session on mount if sessions exist
  useEffect(() => {
    if (hasExistingSession && registrationSessions.length > 0) {
      const lastSession = registrationSessions[registrationSessions.length - 1];
      try {
        populateFieldsFromSessionData(lastSession, lastSession.title);
        setIsCompleted(true);
      } catch (error) {
        console.error('Failed to populate fields from session:', error);
      }
    }
  }, [registrationSessions.length]);

  // Reset isCompleted when URL changes from session URL
  useEffect(() => {
    if (sessionUrl && websiteUrl && websiteUrl !== sessionUrl) {
      setIsCompleted(false);
    }
  }, [websiteUrl, sessionUrl]);

  // Populate all fields from session when user selects "Yes" for AI assistance
  useEffect(() => {
    if (useAI === true && hasExistingSession && registrationSessions.length > 0) {
      const lastSession = registrationSessions[registrationSessions.length - 1];
      try {
        populateFieldsFromSessionData(lastSession, lastSession.title);
        setIsCompleted(true);
      } catch (error) {
        console.error('Failed to populate fields from session:', error);
      }
    }
  }, [useAI]);

  const handleSubmit = async () => {
    setDataSubmitting(true);
    try {
      const resp: SessionData = await fetchData(websiteUrl || '');

      populateFieldsFromSessionData(resp, websiteUrl);

      // Notify parent component about the new session
      if (onSessionCreated) {
        onSessionCreated(resp);
      }

      setDataSubmitting(false);
    } catch (error) {
      console.error('AI website extract failed:', error);
    } finally {
      setIsCompleted(true);
    }
  };

  return (
    <Stack flexDirection="column" gap={1}>
      <Stack
        alignItems="center"
        justifyContent="flex-start"
        direction="row"
        sx={{
          bgcolor: 'primaryLight.50',
          p: 2,
          borderRadius: '4px',
          mb: 2,
        }}
      >
        <Typography color="primaryDark.600">{HELPER_MESSAGES[userValue]}</Typography>
      </Stack>
      <BaseInputLabel text="select your user type" />
      <RadioGroup name="user" sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        <ControlledRadioInput
          name="user"
          radio={{
            label: 'Buyer',
            sx: { ...sxRadio, borderColor: isBuyer ? secondaryGreen[500] : secondaryBlue[100] },
            config: {
              onClick: () => reset(initialBuyerState),
              checked: isBuyer,
            },
          }}
        />
        <ControlledRadioInput
          name="user"
          radio={{
            label: 'Seller',
            sx: { ...sxRadio, borderColor: isSeller ? secondaryGreen[500] : secondaryBlue[100] },
            config: {
              onClick: () => reset(initialSellerState),
              checked: isSeller,
            },
          }}
        />
      </RadioGroup>

      <Divider sx={{ my: 3, borderColor: secondaryBlue[100] }} />

      <Stack flexDirection="column" gap={2}>
        <BaseInputLabel text="Would you like to use AI assistance?" />
        <Stack direction="row" gap={1}>
          <ControlledRadioInput
            name="useAI"
            radio={{
              label: 'Yes',
              sx: {
                ...sxYesNoButton,
                borderColor: useAI === true ? secondaryGreen[500] : secondaryBlue[100],
              },
              config: {
                onClick: () => setUseAI(true),
                checked: useAI === true,
              },
            }}
          />
          <ControlledRadioInput
            name="useAI"
            radio={{
              label: 'No',
              sx: {
                ...sxYesNoButton,
                borderColor: useAI === false ? secondaryGreen[500] : secondaryBlue[100],
              },
              config: {
                onClick: () => setUseAI(false),
                checked: useAI === false,
              },
            }}
          />
        </Stack>

        {useAI && !isLoading && (
          <CustomInput
            input={<CustomInput.Text />}
            name="websiteUrl"
            label="Website URL"
            placeholder="https://example.com"
            required
          />
        )}

        {useAI && isLoading && (
          <Stack
            sx={{
              bgcolor: 'primaryLight.50',
              p: 2,
              borderRadius: '4px',
              mb: 2,
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography color="primaryDark.600" sx={{ fontSize: '14px', fontWeight: 600 }}>
                Gathering company data in progress...
              </Typography>
            </Box>
          </Stack>
        )}

        {useAI && isCompleted && (
          <Stack
            sx={{
              bgcolor: 'primaryLight.50',
              p: 2,
              borderRadius: '4px',
            }}
          >
            <Typography color="primaryDark.600" sx={{ fontSize: '14px', fontWeight: 600 }}>
              ✓ Data gathered! You can now proceed to the next step.
            </Typography>
            <Typography color="primaryDark.500" sx={{ fontSize: '12px', fontStyle: 'italic' }}>
              AI can make mistakes. Please verify important details.
            </Typography>
          </Stack>
        )}

        {useAI && (
          <CustomButton
            onClick={handleSubmit}
            disabled={
              useAI === null ||
              (useAI && (!websiteUrl || isLoading)) ||
              (isCompleted && websiteUrl === sessionUrl) ||
              (hasExistingSession && websiteUrl === sessionUrl)
            }
            sx={{ mt: 1 }}
            loading={isLoading}
          >
            Submit
          </CustomButton>
        )}
      </Stack>
    </Stack>
  );
};

export default User;
